// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { UserMinus, X } from 'lucide-react';
import {
    getProjectMembers,
    addProjectMember,
    removeProjectMember,
    ProjectMembership,
    ProjectMember,
} from '../../apiService';
import DialogShell from '../ui/DialogShell';
import ConfirmDialog from '../ui/ConfirmDialog';

interface ShareProjectDialogProps {
    projectId: string;
    userId: string;
    isOwner: boolean;
    onClose: () => void;
}

export default function ShareProjectDialog({ projectId, userId, isOwner, onClose }: ShareProjectDialogProps) {
    const titleId = React.useId();
    const initialFocusRef = useRef<HTMLInputElement>(null);
    const removalInFlightRef = useRef(false);
    const [membership, setMembership] = useState<ProjectMembership | null>(null);
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [error, setError] = useState('');
    const [pendingRemoval, setPendingRemoval] = useState<ProjectMember | null>(null);
    const [removing, setRemoving] = useState(false);
    const [removeError, setRemoveError] = useState('');
    const [statusMessage, setStatusMessage] = useState('');

    useEffect(() => {
        loadMembers();
    }, [projectId]);

    const loadMembers = async () => {
        setLoading(true);
        const result = await getProjectMembers(projectId, userId);
        if (result.success && result.membership) {
            setMembership(result.membership);
        }
        setLoading(false);
    };

    const handleAdd = async () => {
        if (!email.trim()) return;
        setAdding(true);
        setError('');
        setStatusMessage('');
        const result = await addProjectMember(projectId, userId, email.trim());
        if (result.success) {
            setEmail('');
            await loadMembers();
        } else {
            setError(result.error || 'Failed to add member');
        }
        setAdding(false);
    };

    const requestRemove = (member: ProjectMember) => {
        setPendingRemoval(member);
        setRemoveError('');
        setStatusMessage('');
    };

    const cancelRemove = () => {
        if (removalInFlightRef.current) return;
        setPendingRemoval(null);
        setRemoveError('');
    };

    const confirmRemove = async () => {
        const member = pendingRemoval;
        if (!member || removalInFlightRef.current) return;

        removalInFlightRef.current = true;
        setRemoving(true);
        setRemoveError('');
        try {
            const result = await removeProjectMember(projectId, userId, member.user_id);
            if (result.success) {
                setMembership((current) => current
                    ? {
                        ...current,
                        members: current.members.filter((item) => item.user_id !== member.user_id),
                    }
                    : current);
                setPendingRemoval(null);
                setStatusMessage(`Access removed for ${member.name || member.email}.`);
            } else {
                setRemoveError(result.error || 'Failed to remove member');
            }
        } catch (removeFailure: any) {
            setRemoveError(removeFailure?.message || 'Failed to remove member');
        } finally {
            removalInFlightRef.current = false;
            setRemoving(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !adding) {
            handleAdd();
        }
    };

    return (
        <DialogShell
            open
            onClose={onClose}
            labelledBy={titleId}
            backdropPolicy={email !== '' ? 'static' : 'dismiss'}
            initialFocusRef={initialFocusRef}
            overlayClassName="flex items-center justify-center p-4"
            panelClassName="w-full max-w-md overflow-hidden"
        >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border-default">
                    <h3 id={titleId} className="text-lg font-semibold text-fg">Share project</h3>
                    <button type="button" onClick={onClose} aria-label="Close share project" className="p-1 rounded-full text-fg-tertiary hover:bg-bg-warm">
                        <X size={20} strokeWidth={1.5} aria-hidden="true" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-4 space-y-4">
                    {/* Add member input (owner only) */}
                    {isOwner && (
                        <div>
                            <label className="block text-sm font-medium text-fg-secondary mb-1">Add people</label>
                            <div className="flex gap-2">
                                <input
                                    ref={initialFocusRef}
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="email@company.com"
                                    className="flex-1 px-3 py-2 border border-border-default rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                                    disabled={adding}
                                />
                                <button
                                    onClick={handleAdd}
                                    disabled={adding || !email.trim()}
                                    className="px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-md hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {adding ? '...' : 'Add'}
                                </button>
                            </div>
                            {error && (
                                <p className="mt-1 text-sm text-danger">{error}</p>
                            )}
                        </div>
                    )}

                    {statusMessage && (
                        <p role="status" className="text-sm text-success">
                            {statusMessage}
                        </p>
                    )}

                    {/* Members list */}
                    <div>
                        <label className="block text-sm font-medium text-fg-secondary mb-2">Members</label>
                        {loading ? (
                            <p className="text-sm text-fg-tertiary">Loading...</p>
                        ) : (
                            <div className="space-y-2 max-h-60 overflow-y-auto">
                                {/* Owner */}
                                {membership && (
                                    <div className="flex items-center justify-between px-3 py-2 bg-bg-warm rounded-md">
                                        <div className="flex items-center gap-2">
                                            <div className="w-7 h-7 rounded-full bg-accent-tint text-accent-hover flex items-center justify-center text-xs font-medium">
                                                {(membership.owner_name || membership.owner_email || '?').charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-fg-secondary">
                                                    {membership.owner_name || membership.owner_email || 'Owner'}
                                                </p>
                                                {membership.owner_email && (
                                                    <p className="text-xs text-fg-tertiary">{membership.owner_email}</p>
                                                )}
                                            </div>
                                        </div>
                                        <span className="text-xs text-fg-tertiary font-medium">Owner</span>
                                    </div>
                                )}

                                {/* Members */}
                                {membership?.members.map((member: ProjectMember) => (
                                    <div key={member.user_id} className="flex items-center justify-between px-3 py-2 bg-bg-warm rounded-md">
                                        <div className="flex items-center gap-2">
                                            <div className="w-7 h-7 rounded-full bg-success-bg text-success flex items-center justify-center text-xs font-medium">
                                                {(member.name || member.email).charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-fg-secondary">{member.name || member.email}</p>
                                                <p className="text-xs text-fg-tertiary">{member.email}</p>
                                            </div>
                                        </div>
                                        {isOwner && (
                                            <button
                                                onClick={() => requestRemove(member)}
                                                className="p-1 text-fg-tertiary hover:text-danger hover:bg-danger-bg rounded"
                                                title="Remove member"
                                                aria-label={`Remove ${member.name || member.email}`}
                                            >
                                                <UserMinus size={16} strokeWidth={1.5} aria-hidden="true" />
                                            </button>
                                        )}
                                    </div>
                                ))}

                                {(!membership?.members || membership.members.length === 0) && (
                                    <p className="text-sm text-fg-tertiary py-2">No members added yet</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end p-4 border-t border-border-default">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-bg-warm text-fg-secondary text-sm font-medium rounded-md hover:bg-bg-warm"
                    >
                        Done
                    </button>
                </div>

                <ConfirmDialog
                    open={pendingRemoval !== null}
                    onCancel={cancelRemove}
                    onConfirm={confirmRemove}
                    title={pendingRemoval ? `Remove ${pendingRemoval.name || pendingRemoval.email}?` : ''}
                    description={pendingRemoval
                        ? `They will lose access to "${membership?.project_name || 'this project'}". The project and its content will remain unchanged.`
                        : undefined}
                    error={removeError || undefined}
                    confirmLabel="Remove"
                    variant="destructive"
                    submitting={removing}
                />
        </DialogShell>
    );
}
